import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, HashRouter, useLocation, useNavigate } from "react-router-dom";
import NotFound from "./pages/NotFound";
import ReactGA from 'react-ga4';
import React, { useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import AgentHub from "./pages/AgentHub";
import Dashboard from "./pages/Dashboard";
import TitleManager from "./Services/TitleManager";

ReactGA.initialize('G-9LDQ59773D');

const queryClient = new QueryClient();

// Shim route for direct /oauth2callback hits (e.g., Google redirects)
const OAuth2RedirectShim: React.FC = () => {
  useEffect(() => {
    const q = window.location.search || "";
    const dest = `/#/oauth2callback${q}`;
    if (window.location.hash !== dest) {
      window.location.replace(dest);
    }
  }, []);
  return null;
};

if (typeof window !== "undefined" && window.location.pathname === "/oauth2callback") {
  const q = window.location.search || "";
  const dest = `/#/oauth2callback${q}`;
  if (window.location.hash !== dest) {
    window.location.replace(dest);
  }
}

const OAuthWait: React.FC = () => {
  useEffect(() => {
    const onDone = (ev: MessageEvent) => {
      if (ev?.data?.type === "oauth_done") {
        const ok = !!ev.data?.ok;
        try { window.close(); } catch { }
        if (!window.closed) {
          const suffix = ok ? "auth=ok" : `auth=fail&detail=${encodeURIComponent(JSON.stringify(ev.data?.payload || {}))}`;
          window.location.hash = `#/dashboard?${suffix}`;
        }
      }
    };
    window.addEventListener("message", onDone);
    return () => window.removeEventListener("message", onDone);
  }, []);

  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Authenticating…</h1>
        <p>Please wait while we complete Google sign‑in. This window will close automatically.</p>
      </div>
    </div>
  );
};

const GateOnLocation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.pathname === "/dashboard") {
      const ok = !!localStorage.getItem("userProfile");
      if (!ok) navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

const App = () => {
  const { toast } = useToast();

  // Global OAuth code-exchange handler
  useEffect(() => {
    const getAuthCode = () => {
      const search = new URLSearchParams(window.location.search);
      if (search.get("code")) return search.get("code");
      const hash = window.location.hash || "";
      const idx = hash.indexOf("?");
      if (idx >= 0) {
        const hps = new URLSearchParams(hash.substring(idx + 1));
        return hps.get("code");
      }
      return null;
    };

    const code = getAuthCode();
    if (!code) return;

    const base = (localStorage.getItem("oauth_agent_base") || "").replace(/\/+$/, "");
    if (!base) {
      console.warn("[OAuth] No agent base set in localStorage.oauth_agent_base; cannot exchange code.");
      toast({
        title: "Authentication error",
        description: "Missing agent base URL. Please open an agent and try again.",
        variant: "destructive",
      });
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${base}/auth/code`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code }),
        });

        const status = res.status;
        const raw = await res.text().catch(() => "");
        let payload: any = null;
        try { payload = raw ? JSON.parse(raw) : null; } catch { payload = null; }

        const ok = res.ok;
        const reason =
          (payload && (payload.detail || payload.error || payload.message)) ||
          (raw && raw.slice(0, 400)) ||
          `HTTP ${status}`;

        window.postMessage({ type: "oauth_done", ok, base, status, payload }, "*");
        try { localStorage.setItem("oauth_last_result", JSON.stringify({ base, ok, status, payload, ts: Date.now() })); } catch { }

        if (ok) {
          toast({ title: "Google connected", description: "Your agent is now authenticated." });
        } else {
          toast({
            title: "Authentication failed",
            description: typeof reason === "string" ? reason : "Could not complete Google sign-in.",
            variant: "destructive",
          });
        }
      } catch (e: any) {
        toast({ title: "Authentication error", description: e?.message || String(e), variant: "destructive" });
      } finally {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          url.searchParams.delete("scope");
          if (url.hash.includes("?")) {
            const [path, q] = url.hash.split("?");
            const hps = new URLSearchParams(q);
            hps.delete("code"); hps.delete("state"); hps.delete("scope");
            url.hash = `${path}${hps.toString() ? "?" + hps.toString() : ""}`;
          }
          window.history.replaceState({}, "", url.toString());
        } catch { }
      }
    })();
  }, [toast]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/oauth2callback" element={<OAuth2RedirectShim />} />
          </Routes>
        </BrowserRouter>
        <HashRouter>
          <GateOnLocation />
          <TitleManager />
          <Routes>
            <Route path="/" element={<AgentHub />} />
            <Route path="/emailtracker" element={<AgentHub />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/oauth2callback" element={<OAuthWait />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
